import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Stack,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  Collapse,
  IconButton,
  InputAdornment,
  alpha,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { ScheduledCall, OutboundCallConfig } from '../types';
import { callSchedulingApi } from '../services/api';
import { format } from 'date-fns';
import AgentSelect from './AgentSelect';
import PhoneNumberSelect, { PhoneChannel, fetchPhoneChannels } from './PhoneNumberSelect';
import TimeInput from './TimeInput';
import { internalChannelsApi } from '../services/api';
import { compileTemplate } from '../utils/templateCompiler';
import { partialAgentUpdateApi } from '../services/api';

// Default values for the form
const defaultOutboundCallConfig: OutboundCallConfig = {
  calling_hours: {
    timezone: 'America/Los_Angeles',
    weekly_hours: {
      monday: { start_time: '09:00:00', end_time: '17:00:00' },
      tuesday: { start_time: '09:00:00', end_time: '17:00:00' },
      wednesday: { start_time: '09:00:00', end_time: '17:00:00' },
      thursday: { start_time: '09:00:00', end_time: '17:00:00' },
      friday: { start_time: '09:00:00', end_time: '17:00:00' }
    },
    special_closures: {}
  }
};

// Days of the week for calling hours
const daysOfWeek = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

interface ScheduleCallFormProps {
  onSuccess?: (result: any) => void;
  initialConfig?: { fromNumber: string; channelId: number; agentId: number | null } | null;
}

const ScheduleCallForm: React.FC<ScheduleCallFormProps> = ({ onSuccess, initialConfig }): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [enabledDays, setEnabledDays] = useState<Record<string, boolean>>({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false
  });
  const [closureDate, setClosureDate] = useState<Date | null>(null);
  const [closureStartTime, setClosureStartTime] = useState<Date | null>(null);
  const [closureEndTime, setClosureEndTime] = useState<Date | null>(null);
  const [useCustomHours, setUseCustomHours] = useState(false);
  const [useRetryConfig, setUseRetryConfig] = useState(false);
  const [useVoicemailHandling, setUseVoicemailHandling] = useState(false);
  const [voicemailOption, setVoicemailOption] = useState<{ type: 'static_text' | 'hangup'; text?: string }>({ type: 'static_text', text: '' });
  const [useGreetingInstructions, setUseGreetingInstructions] = useState(false);
  const [callGreeting, setCallGreeting] = useState('');
  const [callInstructions, setCallInstructions] = useState('');

  // Get saved config from localStorage
  const getSavedConfig = (): Partial<ScheduledCall> => {
    if (initialConfig) {
      return {
        from_number: initialConfig.fromNumber,
        to_number: '',
        context_parameters: {},
        agent_id: initialConfig.agentId,
        internal_channel_id: initialConfig.channelId,
        schedule_dt: null,
        outbound_call_config: defaultOutboundCallConfig,
        immediate: false
      };
    }
    const savedConfig = localStorage.getItem('scheduleConfig');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        return {
          from_number: config.fromNumber || '',
          to_number: '',
          context_parameters: {},
          agent_id: config.agentId,
          internal_channel_id: config.channelId || 0,
          schedule_dt: null,
          outbound_call_config: defaultOutboundCallConfig,
          immediate: false
        };
      } catch (e) {
        console.error('Error parsing saved config:', e);
      }
    }
    return {
      from_number: '',
      to_number: '',
      context_parameters: {},
      agent_id: null,
      internal_channel_id: 0,
      schedule_dt: null,
      outbound_call_config: defaultOutboundCallConfig,
      immediate: false
    };
  };

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<ScheduledCall>({
    defaultValues: getSavedConfig(),
    resolver: (values) => {
      const errors: Record<string, { type: string; message: string }> = {};
      if (!values.to_number) {
        errors.to_number = {
          type: 'required',
          message: 'To number is required'
        };
      }
      return {
        values,
        errors
      };
    }
  });

  // Save config to localStorage whenever it changes
  useEffect(() => {
    const fromNumber = watch('from_number');
    const agentId = watch('agent_id');
    const channelId = watch('internal_channel_id');

    if (fromNumber && agentId && channelId) {
      localStorage.setItem('scheduleConfig', JSON.stringify({
        fromNumber,
        agentId,
        channelId
      }));
    }
  }, [watch('from_number'), watch('agent_id'), watch('internal_channel_id')]);

  const immediate = watch('immediate');

  // Channels are prefetched when navigating to schedule page, no need to fetch again

  // Convert time object to string format HH:MM:SS
  const formatTimeToString = (time: Date | null): string => {
    if (!time) return '00:00:00';
    return format(time, 'HH:mm:ss');
  };

  // Convert string time to Date object
  const parseTimeString = (timeStr: string): Date => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds);
    return date;
  };

  // Handle day toggle
  const handleDayToggle = (day: string, checked: boolean) => {
    setEnabledDays(prev => ({ ...prev, [day]: checked }));
    
    // If day is being disabled, remove it from the config
    if (!checked) {
      // Get current config and ensure it's an object
      const configValue = watch('outbound_call_config');
      const currentConfig = typeof configValue === 'string' 
        ? JSON.parse(configValue) as OutboundCallConfig
        : { ...configValue as OutboundCallConfig };
        
      if (currentConfig.calling_hours?.weekly_hours) {
        const newWeeklyHours = { ...currentConfig.calling_hours.weekly_hours };
        delete newWeeklyHours[day];
        
        setValue('outbound_call_config', {
          ...currentConfig,
          calling_hours: {
            ...currentConfig.calling_hours,
            weekly_hours: newWeeklyHours
          }
        });
      }
    } else {
      // If day is being enabled, add default hours
      // Get current config and ensure it's an object
      const configValue = watch('outbound_call_config');
      const currentConfig = typeof configValue === 'string' 
        ? JSON.parse(configValue) as OutboundCallConfig
        : { ...configValue as OutboundCallConfig };
        
      if (currentConfig.calling_hours?.weekly_hours) {
        setValue('outbound_call_config', {
          ...currentConfig,
          calling_hours: {
            ...currentConfig.calling_hours,
            weekly_hours: {
              ...currentConfig.calling_hours.weekly_hours,
              [day]: { start_time: '09:00:00', end_time: '17:00:00' }
            }
          }
        });
      }
    }
  };

  // Handle form submission
  const onSubmit = async (data: ScheduledCall) => {
    try {
      setLoading(true);
      setError(null);

      // Ensure outbound_call_config is an object
      if (typeof data.outbound_call_config === 'string') {
        try {
          data.outbound_call_config = JSON.parse(data.outbound_call_config) as OutboundCallConfig;
        } catch (e) {
          setError('Invalid JSON in outbound_call_config');
          setLoading(false);
          return;
        }
      }

      // Only include calling hours if custom hours are enabled
      if (!useCustomHours) {
        if (data.outbound_call_config) {
          delete data.outbound_call_config.calling_hours;
        }
      }

      // Only include retry config if enabled
      if (!useRetryConfig) {
        if (data.outbound_call_config) {
          delete data.outbound_call_config.retry_config;
        }
      }
      
      // Add voicemail_option if enabled - send template as-is (with {var} placeholders)
      // Backend will convert to {{var}} format and handle dynamic variables
      if (useVoicemailHandling && voicemailOption.type === 'static_text' && voicemailOption.text) {
        const config = data.outbound_call_config as OutboundCallConfig;
        // Send template as user entered it - backend handles conversion
        config.voicemail_option = {
          action: {
            type: 'static_text',
            text: voicemailOption.text
          }
        };
      } else if (useVoicemailHandling && voicemailOption.type === 'hangup') {
        const config = data.outbound_call_config as OutboundCallConfig;
        config.voicemail_option = {
          action: {
            type: 'hangup'
          }
        };
      } else {
        // Remove voicemail_option if disabled
        if (data.outbound_call_config) {
          delete (data.outbound_call_config as any).voicemail_option;
        }
      }
      
      // Ensure agent_id is in outbound_call_config
      const config = data.outbound_call_config as OutboundCallConfig;
      if (!config.agent_id) {
        config.agent_id = data.agent_id;
      }

      // Convert context_parameters from string to object if needed
      if (typeof data.context_parameters === 'string') {
        try {
          data.context_parameters = JSON.parse(data.context_parameters as unknown as string);
        } catch (e) {
          setError('Invalid JSON in context parameters');
          setLoading(false);
          return;
        }
      } else if (data.context_parameters === null || data.context_parameters === undefined) {
        // Ensure context_parameters is at least an empty object
        data.context_parameters = {};
      }

      // Handle greeting and instructions - compile templates and add to config
      if (useGreetingInstructions && (callGreeting || callInstructions)) {
        const contextParams = data.context_parameters as Record<string, any> || {};
        try {
          // Compile templates on backend
          const compiled = await partialAgentUpdateApi.compileTemplates(
            callGreeting || undefined,
            callInstructions || undefined,
            contextParams
          );
          
          // Add compiled values to outbound_call_config
          if (compiled.outbound_greeting) {
            config.outbound_greeting = compiled.outbound_greeting;
          }
          if (compiled.extra_instruction) {
            config.extra_instructions = compiled.extra_instruction;
          }
        } catch (e: any) {
          setError(`Failed to compile templates: ${e.message || 'Unknown error'}`);
          setLoading(false);
          return;
        }
      } else {
        // Remove greeting and instructions fields if disabled
        if (config.outbound_greeting) {
          delete config.outbound_greeting;
        }
        if (config.extra_instructions) {
          delete config.extra_instructions;
        }
      }

      const response = await callSchedulingApi.scheduleCall(data);
      setSuccess('Call scheduled successfully!');
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to schedule call');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ maxWidth: 800, mx: 'auto' }}>
      <Stack spacing={4}>
        {/* Basic Info Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
            Call Details
          </Typography>
          
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <PhoneNumberSelect
                  value={watch('from_number')}
                  onChange={(value, channelId) => {
                    setValue('from_number', value);
                    setValue('internal_channel_id', parseInt(channelId));
                  }}
                  label="From Number"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="To Number"
                  fullWidth
                  value={watch('to_number') || ''}
                  onChange={(e) => setValue('to_number', e.target.value)}
                  error={!!errors.to_number}
                  helperText={errors.to_number?.message}
                  placeholder="+15551234567"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: alpha('#000', 0.02),
                      '&:hover': {
                        backgroundColor: alpha('#000', 0.03),
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="agent_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <AgentSelect
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      label="Agent"
                      error={!!error}
                      helperText={error ? error.message : ''}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Context Parameters */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mt: 2 }}>
                Context Parameters
              </Typography>
              <Stack spacing={2}>
                {Object.entries(watch('context_parameters') || {}).map(([key, value], index) => (
                  <Paper
                    key={index}
                    sx={{
                      p: 2,
                      backgroundColor: alpha('#000', 0.02),
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      size="small"
                      label="Key"
                      value={key}
                      onChange={(e) => {
                        const params = { ...(watch('context_parameters') as Record<string, string>) };
                        const oldValue = params[key];
                        delete params[key];
                        params[e.target.value] = oldValue;
                        setValue('context_parameters', params);
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Value"
                      value={value}
                      onChange={(e) => {
                        const params = { ...(watch('context_parameters') as Record<string, string>) };
                        params[key] = e.target.value;
                        setValue('context_parameters', params);
                      }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const params = { ...(watch('context_parameters') as Record<string, string>) };
                        delete params[key];
                        setValue('context_parameters', params);
                      }}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const params = { ...(watch('context_parameters') as Record<string, string>) };
                    let newKey = 'new_key';
                    let counter = 1;
                    while (params[newKey]) {
                      newKey = `new_key_${counter}`;
                      counter++;
                    }
                    params[newKey] = '';
                    setValue('context_parameters', params);
                  }}
                  sx={{
                    borderStyle: 'dashed',
                    color: 'text.secondary',
                  }}
                >
                  Add Context Parameter
                </Button>
              </Stack>
            </Grid>

            <Controller
              name="schedule_dt"
              control={control}
              render={({ field }) => (
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <DatePicker
                      label="Schedule Date"
                      value={field.value ? new Date(field.value) : null}
                      onChange={(newDate) => {
                        if (newDate && !isNaN(newDate.getTime())) {
                          const currentDate = field.value ? new Date(field.value) : new Date();
                          const updatedDate = new Date(newDate);
                          updatedDate.setHours(currentDate.getHours());
                          updatedDate.setMinutes(currentDate.getMinutes());
                          field.onChange(updatedDate.toISOString());
                        } else {
                          field.onChange(null);
                        }
                      }}
                      disabled={watch('immediate')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!(field.value && isNaN(new Date(field.value).getTime())),
                          helperText: field.value && isNaN(new Date(field.value).getTime()) ? 'Invalid date' : '',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TimeInput
                      value={field.value ? new Date(field.value) : null}
                      onChange={(newDate) => {
                        if (newDate && !isNaN(newDate.getTime())) {
                          field.onChange(newDate.toISOString());
                        } else {
                          field.onChange(null);
                        }
                      }}
                      disabled={watch('immediate')}
                    />
                  </Grid>
                </Grid>
              )}
            />

            <FormControlLabel
              control={
                <Controller
                  name="immediate"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              }
              label="Schedule Immediately"
            />
          </Stack>
        </Box>

        {/* Advanced Settings Toggle */}
        <Box>
          <Button
            onClick={() => setShowAdvanced(!showAdvanced)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.3s',
                }}
              />
            }
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: alpha('#1e293b', 0.04),
              },
            }}
          >
            <SettingsIcon sx={{ mr: 1 }} />
            Advanced Settings
          </Button>

          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 3 }}>
              {/* Custom Hours Toggle */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustomHours}
                      onChange={(e) => setUseCustomHours(e.target.checked)}
                    />
                  }
                  label="Use Custom Calling Hours"
                />
              </Box>

              {/* Calling Hours Section - Only show if enabled */}
              <Collapse in={useCustomHours}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mt: 2 }}>
                  Calling Hours
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="outbound_call_config.calling_hours.timezone"
                      control={control}
                      defaultValue="America/Los_Angeles"
                      render={({ field }) => (
                        <TextField
                          select
                          fullWidth
                          label="Timezone"
                          {...field}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: alpha('#1e293b', 0.02),
                              '&:hover': {
                                backgroundColor: alpha('#1e293b', 0.03),
                              },
                              '&.Mui-focused': {
                                backgroundColor: '#fff',
                              },
                            },
                          }}
                        >
                          <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                          <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                          <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                          <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                          <MenuItem value="America/Anchorage">Alaska Time (AKT)</MenuItem>
                          <MenuItem value="Pacific/Honolulu">Hawaii Time (HT)</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} key={'monday'}>
                      <FormControlLabel
                        control={
                          <Switch
                          checked={enabledDays['monday']}
                          onChange={(e) => handleDayToggle('monday', e.target.checked)}
                          />
                        }
                      label={'Monday'}
                      />
                      
                    {enabledDays['monday'] && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.monday.start_time`}
                            control={control}
                            render={({ field }) => (
                              <TimePicker
                                label="Start Time"
                                value={parseTimeString(field.value)}
                                onChange={(newValue) => {
                                  field.onChange(formatTimeToString(newValue));
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    size: "small",
                                    sx: {
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha('#000', 0.02)
                                      }
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                          
                          <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.monday.end_time`}
                            control={control}
                            render={({ field }) => (
                              <TimePicker
                                label="End Time"
                                value={parseTimeString(field.value)}
                                onChange={(newValue) => {
                                  field.onChange(formatTimeToString(newValue));
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    size: "small",
                                    sx: {
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha('#000', 0.02)
                                      }
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </Stack>
                      )}
                    </Grid>
                  <Grid item xs={12} sm={6} key={'tuesday'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabledDays['tuesday']}
                          onChange={(e) => handleDayToggle('tuesday', e.target.checked)}
                        />
                      }
                      label={'Tuesday'}
                    />
                    
                    {enabledDays['tuesday'] && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.tuesday.start_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="Start Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                        
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.tuesday.end_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="End Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} key={'wednesday'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabledDays['wednesday']}
                          onChange={(e) => handleDayToggle('wednesday', e.target.checked)}
                        />
                      }
                      label={'Wednesday'}
                    />
                    
                    {enabledDays['wednesday'] && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.wednesday.start_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="Start Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                        
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.wednesday.end_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="End Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} key={'thursday'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabledDays['thursday']}
                          onChange={(e) => handleDayToggle('thursday', e.target.checked)}
                        />
                      }
                      label={'Thursday'}
                    />
                    
                    {enabledDays['thursday'] && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.thursday.start_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="Start Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                        
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.thursday.end_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="End Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} key={'friday'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabledDays['friday']}
                          onChange={(e) => handleDayToggle('friday', e.target.checked)}
                        />
                      }
                      label={'Friday'}
                    />
                    
                    {enabledDays['friday'] && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.friday.start_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="Start Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                        
                        <Controller
                          name={`outbound_call_config.calling_hours.weekly_hours.friday.end_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label="End Time"
                              value={parseTimeString(field.value)}
                              onChange={(newValue) => {
                                field.onChange(formatTimeToString(newValue));
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02)
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} key={'saturday'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabledDays['saturday']}
                          onChange={(e) => handleDayToggle('saturday', e.target.checked)}
                        />
                      }
                      label={'Saturday'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} key={'sunday'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabledDays['sunday']}
                          onChange={(e) => handleDayToggle('sunday', e.target.checked)}
                        />
                      }
                      label={'Sunday'}
                    />
                  </Grid>
                </Grid>

                {/* Special Closures */}
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                    Special Closures
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name="outbound_call_config.calling_hours.special_closures"
                        control={control}
                        render={({ field }) => (
                          <>
                            <DatePicker
                              label="Closure Date"
                              value={closureDate}
                              onChange={setClosureDate}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.02),
                                    },
                                  },
                                },
                              }}
                            />
                            {closureDate && (
                              <Stack spacing={2} sx={{ mt: 2 }}>
                                <TimePicker
                                  label="Start Time"
                                  value={closureStartTime}
                                  onChange={setClosureStartTime}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      sx: {
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: alpha('#000', 0.02),
                                        },
                                      },
                                    },
                                  }}
                                />
                                <TimePicker
                                  label="End Time"
                                  value={closureEndTime}
                                  onChange={setClosureEndTime}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      sx: {
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: alpha('#000', 0.02),
                                        },
                                      },
                                    },
                                  }}
                                />
                                <Button
                                  variant="outlined"
                                  onClick={() => {
                                    if (closureDate && closureStartTime && closureEndTime) {
                                      const dateStr = format(closureDate, 'yyyy-MM-dd');
                                      const newClosures = {
                                        ...field.value,
                                        [dateStr]: {
                                          start_time: format(closureStartTime, 'HH:mm:ss'),
                                          end_time: format(closureEndTime, 'HH:mm:ss'),
                                        },
                                      };
                                      field.onChange(newClosures);
                                      setClosureDate(null);
                                      setClosureStartTime(null);
                                      setClosureEndTime(null);
                                    }
                                  }}
                                  sx={{
                                    borderStyle: 'dashed',
                                    color: 'text.secondary',
                                  }}
                                >
                                  Add Closure
                                </Button>
                              </Stack>
                            )}
                            
                            {/* Display existing closures */}
                            {Object.entries(field.value || {}).length > 0 && (
                              <Stack spacing={1} sx={{ mt: 2 }}>
                                {Object.entries(field.value || {}).map(([date, times]: [string, any]) => (
                                  <Paper
                                    key={date}
                                    sx={{
                                      p: 2,
                                      backgroundColor: alpha('#000', 0.02),
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {format(new Date(date), 'MMM d, yyyy')} ({times.start_time} - {times.end_time})
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const newClosures = { ...field.value };
                                        delete newClosures[date];
                                        field.onChange(newClosures);
                                      }}
                                      sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                          color: 'error.main',
                                        },
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Paper>
                                ))}
                              </Stack>
                            )}
                          </>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>

              {/* Voicemail Handling Toggle */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useVoicemailHandling}
                      onChange={(e) => setUseVoicemailHandling(e.target.checked)}
                    />
                  }
                  label="Voicemail Handling"
                />

                <Collapse in={useVoicemailHandling}>
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ position: 'relative' }}>
                          <FormControl fullWidth>
                            <InputLabel>Voicemail Action</InputLabel>
                            <Select
                              value={voicemailOption.type}
                              label="Voicemail Action"
                              onChange={(e) => setVoicemailOption(prev => ({
                                ...prev,
                                type: e.target.value as 'static_text' | 'hangup'
                              }))}
                            >
                              <MenuItem value="static_text">Leave Static Message</MenuItem>
                              <MenuItem value="hangup">Hang Up</MenuItem>
                            </Select>
                          </FormControl>
                          <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <Tooltip title="How the agent should handle voicemail detection. Static message supports template variables like {caller_name} that will be replaced with actual values from context parameters. Hangup will immediately end the call." arrow>
                              <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18, pointerEvents: 'auto' }} />
                            </Tooltip>
                          </Box>
                        </Box>
                      </Grid>
                      {voicemailOption.type === 'static_text' && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                              Available Variables (click to insert):
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                              {Object.keys(watch('context_parameters') || {}).map(key => ({
                                display: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                                value: `{${key}}`
                              })).map((variable) => (
                                <Chip
                                  key={variable.value}
                                  label={variable.display}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    const currentText = voicemailOption.text || '';
                                    setVoicemailOption(prev => ({
                                      ...prev,
                                      text: currentText + variable.value
                                    }));
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              ))}
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <Tooltip 
                              title="Template message with variables. Use {variableName} format. Variables will be replaced with actual values from context parameters when the call is scheduled."
                              arrow
                            >
                              <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Voicemail Message Template"
                                value={voicemailOption.text || ''}
                                onChange={(e) => setVoicemailOption(prev => ({
                                  ...prev,
                                  text: e.target.value
                                }))}
                                placeholder="Hi {caller_name}, please give us a callback tomorrow at 10am."
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Tooltip>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Greeting and Instructions Toggle */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={useGreetingInstructions}
                onChange={(e) => setUseGreetingInstructions(e.target.checked)}
              />
            }
            label="Greeting and Instructions"
          />
          <Tooltip 
            title="Set a custom greeting and instructions for this call only. These changes do not modify the agent permanently."
            arrow
          >
            <InfoIcon sx={{ ml: 1, fontSize: 18, color: alpha('#000', 0.4), verticalAlign: 'middle' }} />
          </Tooltip>

          <Collapse in={useGreetingInstructions}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Greeting Variables (click to insert):
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    {Object.keys(watch('context_parameters') || {}).map(key => ({
                      display: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                      value: `{${key}}`
                    })).map((variable) => (
                      <Chip
                        key={variable.value}
                        label={variable.display}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const currentGreeting = callGreeting || '';
                          setCallGreeting(currentGreeting + variable.value);
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Greeting"
                    value={callGreeting}
                    onChange={(e) => setCallGreeting(e.target.value)}
                    placeholder="Hi {caller_name}, this is a call from..."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip 
                            title="The greeting for this call. Use {variableName} format for template variables."
                            arrow
                          >
                            <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Instructions Variables (click to insert):
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    {Object.keys(watch('context_parameters') || {}).map(key => ({
                      display: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                      value: `{${key}}`
                    })).map((variable) => (
                      <Chip
                        key={variable.value}
                        label={variable.display}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const currentInstructions = callInstructions || '';
                          setCallInstructions(currentInstructions + variable.value);
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Instructions"
                    value={callInstructions}
                    onChange={(e) => setCallInstructions(e.target.value)}
                    placeholder="Remember to mention {appointment_date} when speaking with {caller_name}..."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip 
                            title="Additional instructions for the agent for this call. Use {variableName} format for template variables."
                            arrow
                          >
                            <InfoIcon sx={{ color: alpha('#000', 0.4), fontSize: 18 }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        {/* Submit Button */}
        <Box sx={{ mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading}
            sx={{
              py: 2,
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Schedule Call'}
          </Button>
        </Box>
      </Stack>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScheduleCallForm; 